import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getMeta } from "@/lib/api";
import { useAppStore, SCENARIO_CAPTIONS } from "@/store/appStore";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Users, HardHat, Briefcase, Boxes, ClipboardCheck, Share2,
  TriangleAlert, FileText, Settings, Target, CloudLightning, RefreshCw, SlidersHorizontal,
  Check, ChevronDown, HelpCircle, BookOpen, GitCompareArrows, LogOut, UserCircle,
} from "lucide-react";
import { useAuth } from "@/store/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, title: "Overview - Overall Readiness",
    subtitle: "Executive view of our ability to serve Subscribers, support Technicians and inform Executives",
    mission: "Give leadership a single, defensible view of storm readiness across every system." },
  { to: "/subscriber", label: "Subscriber Readiness", icon: Users, title: "SUBSCRIBER READINESS",
    subtitle: "Our ability to keep customers informed, connected and safe.",
    mission: "Keep our customers informed, connected and safe." },
  { to: "/technician", label: "Technician Readiness", icon: HardHat, title: "TECHNICIAN READINESS",
    subtitle: "Our ability to support field crews restoring service.",
    mission: "Deliver safe, reliable energy and service when it matters most." },
  { to: "/executive", label: "Executive Readiness", icon: Briefcase, title: "EXECUTIVE READINESS",
    subtitle: "Our ability to inform leadership decisions in real time.",
    mission: "Give leadership the truth, fast, to make the right calls." },
  { to: "/shared", label: "Shared / Infrastructure", testId: "nav-shared", icon: Share2, title: "SHARED / INFRASTRUCTURE",
    subtitle: "Platform and infrastructure serving every stream.",
    mission: "Keep the shared backbone strong so every stream stays up." },
  { to: "/applications", label: "Applications", icon: Boxes, title: "APPLICATIONS",
    subtitle: "The full CenterPoint resiliency application inventory.",
    mission: "Every application, scored and traceable to its evidence." },
  { to: "/scenarios", label: "Scenario Comparison", icon: GitCompareArrows, title: "SCENARIO COMPARISON",
    subtitle: "Side-by-side readiness across Blue Sky, Gray Sky, Black Sky and Beyond Black Sky.",
    mission: "See how readiness holds up as conditions escalate." },
  { to: "/testing", label: "Testing & Quality", icon: ClipboardCheck, title: "TESTING & QUALITY",
    subtitle: "Test coverage, recency, evidence quality and defects.",
    mission: "Prove readiness with credible, current evidence." },
  { to: "/reports", label: "Reports", icon: FileText, title: "REPORTS",
    subtitle: "Branded, scheduled readiness reporting for every audience.",
    mission: "The right report, to the right audience, on the right cadence." },
  { to: "/admin", label: "Administration", icon: Settings, title: "ADMINISTRATION",
    subtitle: "Scoring model, users, data sources and ingest management.",
    mission: "Configure the model; keep the audit trail honest." },
];

const SCEN_STYLE = {
  BLUE_SKY: "#0075C9", GRAY_SKY: "#6B7280", BLACK_SKY: "#1F2937", BEYOND_BLACK_SKY: "#D6337A",
};

function ScenarioSelector() {
  const { scenario, setScenario } = useAppStore();
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });
  const scenarios = meta?.scenarios || [];
  const current = scenarios.find((s) => s.key === scenario);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="scenario-selector"
          className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 hover:bg-white/10 transition-colors">
          <CloudLightning size={18} style={{ color: SCEN_STYLE[scenario] === "#1F2937" ? "#9BA3AF" : SCEN_STYLE[scenario] }} />
          <div className="text-left leading-tight">
            <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: scenario === "BEYOND_BLACK_SKY" ? "#F58FBB" : "#fff" }}>
              {current?.label || "Beyond Black Sky"}
            </div>
            <div className="text-[10px] text-white/60">{SCENARIO_CAPTIONS[scenario]}</div>
          </div>
          <ChevronDown size={14} className="text-white/60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {scenarios.map((s) => (
          <DropdownMenuItem key={s.key} data-testid={`scenario-${s.key}`} onClick={() => setScenario(s.key)}
            className="flex items-start gap-2 cursor-pointer py-2">
            <CloudLightning size={16} className="mt-0.5" style={{ color: SCEN_STYLE[s.key] === "#1F2937" ? "#6B7280" : SCEN_STYLE[s.key] }} />
            <div className="flex-1">
              <div className="text-sm font-semibold">{s.label}</div>
              <div className="text-[11px] text-cnp-textMuted">{SCENARIO_CAPTIONS[s.key]}</div>
            </div>
            {scenario === s.key && <Check size={16} className="text-cnp-blue mt-0.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppShell({ children }) {
  const location = useLocation();
  const active = NAV.find((n) => n.to === location.pathname) || NAV[0];
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });
  const openExplain = useAppStore((s) => s.openExplain);
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex bg-cnp-bg">
      {/* Left rail */}
      <aside className="w-[210px] shrink-0 bg-cnp-navy text-white flex flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-4 py-4 border-b border-white/10">
          <div className="bg-white rounded-lg px-3 py-2 flex items-center justify-center">
            <img src="/centerpoint-logo.png" alt="CenterPoint Energy" className="h-16 w-auto object-contain" />
          </div>
          <div className="text-[10px] text-white/50 text-center mt-1.5 uppercase tracking-wider">TMO Resiliency</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end data-testid={n.testId || `nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                cn("relative flex items-center gap-3 px-4 py-2.5 text-[13px] transition-colors",
                  isActive ? "bg-cnp-navyLight text-white font-semibold" : "text-white/70 hover:text-white hover:bg-white/5")}>
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-0 bottom-0 w-1 bg-cnp-blue rounded-r" />}
                  <n.icon size={17} strokeWidth={2} />
                  <span>{n.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 space-y-3">
          <div className="rounded-lg bg-white/5 border border-white/10 p-3">
            <div className="flex items-center gap-2 text-cnp-amber mb-1.5">
              <Target size={15} />
              <span className="text-[11px] font-bold uppercase tracking-wide">Mission</span>
            </div>
            <p className="text-[11px] text-white/70 leading-snug">{active.mission}</p>
          </div>
          <div className="rounded-lg bg-white p-2.5 flex flex-col items-center" data-testid="powered-by-rts">
            <span className="text-[9px] uppercase tracking-wider text-cnp-textMuted mb-1">Powered by</span>
            <img src="/rts-logo.png" alt="Resolve Tech Solutions" className="h-11 w-auto object-contain" />
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-[210px] flex flex-col min-h-screen">
        {/* Persistent brand bar + page header (sticky as one unit) */}
        <div className="sticky top-0 z-20 shadow-md">
          <div className="bg-cnp-navy text-white px-6 pt-3 pb-2.5 flex items-center gap-3 border-b border-white/10" data-testid="app-title-bar">
            <span className="h-10 w-10 rounded-lg bg-cnp-blue/20 text-cnp-blue flex items-center justify-center shrink-0">
              <CloudLightning size={22} />
            </span>
            <div className="leading-tight">
              <h1 className="text-xl font-extrabold tracking-tight leading-none" data-testid="app-title">E.A.R.S</h1>
              <p className="text-xs text-white/60 mt-1" data-testid="app-subtitle">Enterprise Application Readiness Status</p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <span className="rounded-full bg-cnp-amber/90 text-[#3A2A00] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1" data-testid="demo-pill-shell">
                Demo Environment
              </span>
              {user && (
                <div className="flex items-center gap-2 border-l border-white/10 pl-3">
                  <UserCircle size={22} className="text-white/70" />
                  <div className="leading-tight text-right">
                    <div className="text-xs font-semibold" data-testid="user-name">{user.name}</div>
                    <div className="text-[10px] text-cnp-blue uppercase tracking-wide" data-testid="user-role">{user.role}</div>
                  </div>
                  <button onClick={logout} data-testid="logout-button"
                    className="ml-1 flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 px-2.5 py-1.5 text-[11px] font-semibold transition-colors">
                    <LogOut size={13} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
          <header className="bg-cnp-navy text-white px-6 py-2.5 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold tracking-wide uppercase leading-tight text-white/90">{active.title}</h2>
              <p className="text-xs text-white/55">{active.subtitle}</p>
            </div>
            <div className="flex items-center gap-4">
              <ScenarioSelector />
              <div className="hidden lg:block text-right leading-tight border-l border-white/10 pl-4">
                <div className="text-[10px] text-white/50 uppercase tracking-wide">Data as of</div>
                <div className="text-xs font-semibold tnum">{meta?.as_of || "—"}</div>
              </div>
              <div className="hidden xl:flex items-center gap-1.5 text-[11px] text-white/60">
                <RefreshCw size={13} /> Auto refresh · 5 min ago
              </div>
              <button data-testid="filters-button"
                className="flex items-center gap-1.5 rounded-lg bg-cnp-blue px-3 py-1.5 text-xs font-semibold hover:bg-cnp-blue/90 transition-colors">
                <SlidersHorizontal size={14} /> Filters
              </button>
            </div>
          </header>
          <div data-testid="sample-data-banner" className="bg-cnp-amber text-[#3A2A00] text-[11px] font-extrabold uppercase tracking-[0.15em] px-6 py-1.5 flex items-center justify-center gap-2 border-b border-black/10">
            <TriangleAlert size={13} strokeWidth={2.5} />
            Sample Data — all figures are illustrative and for demonstration only
          </div>
        </div>

        <main className="flex-1 p-6">{children}</main>

        {/* Footer */}
        <footer className="bg-cnp-card border-t border-cnp-border px-6 py-3 flex flex-wrap items-center justify-between gap-3 text-xs text-cnp-textMuted">
          <div className="flex items-center gap-4 flex-wrap">
            {(meta?.confidence_legend || []).map((l) => (
              <span key={l.status} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{
                  backgroundColor: { READY: "#00A651", AT_RISK: "#F5A623", NOT_READY: "#D0021B", NA: "#9BA3AF" }[l.status] }} />
                <span className="font-medium text-cnp-text">{l.label}</span> {l.rule}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline">Confidence is calculated using weighted factors across testing, quality signals and dependency health.</span>
            <button data-testid="how-calculated-footer" onClick={() => openExplain({ type: "overview", title: "Overall Readiness" })}
              className="flex items-center gap-1 text-cnp-blue font-semibold hover:underline">
              <HelpCircle size={13} /> How is this calculated?
            </button>
            <button className="flex items-center gap-1 text-cnp-blue font-semibold hover:underline">
              <BookOpen size={13} /> Glossary
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
